import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowUpRight, FolderOpen, HardHat, Plus, Save, Sparkles, Trash2, X } from "lucide-react";
import { practiceApi, formatDate } from "../lib/practiceApi";
import { StatusBadge } from "../components/StatusBadge";
import { SortableTh } from "../components/sorting/SortableTh";
import { sortRows, useSortPreference } from "../lib/sorting";
import "./pos.css";

const COLUMNS = [
  ["client_name", "Ragione sociale", "text"], ["referent", "Referente", "text"], ["start_date", "Inizio convenzione", "date"],
  ["last_renewal_date", "Ultimo rinnovo", "date"], ["next_due_date", "Prossima scadenza", "deadline"],
  ["days_remaining", "Giorni", "number", "numeric"], ["service_type", "Tipo di convenzione", "text"],
  ["status", "Stato", "state"], ["documents_count", "Documenti", "number"],
];
const DEFAULT_SORT = { key: "next_due_date", kind: "deadline", dir: "asc" };

const FALLBACK_SERVICES = ["Redazione POS e PiMUS", "Redazione POS", "Redazione PiMUS", "Consulenza sicurezza continuativa", "RSPP esterno", "Formazione e aggiornamento", "Aggiornamento documentale", "Altro"];
const CYCLES = ["Annuale", "Semestrale", "Trimestrale", "Mensile", "Una tantum"];
const MODES = [["blank", "Partire da zero"], ["duplicate", "Duplicare l'ultima versione"],
               ["registry", "Usare i dati dell'anagrafica aziendale"], ["ai", "Aggiornamento AI del documento precedente"]];
const SECTION_LABELS = [["azienda", "Dati azienda"], ["organigramma", "Organigramma sicurezza"], ["lavorazioni", "Lavorazioni"],
                        ["rischi", "Rischi e misure"], ["dpi", "DPI"], ["procedure", "Procedure"],
                        ["attrezzature", "Attrezzature"], ["note", "Note"]];
const FILTERS = [["all", "Tutte"], ["REGOLARE", "Regolari"], ["IN SCADENZA", "In scadenza"], ["URGENTE", "Urgenti"], ["SCADUTA", "Scadute"]];

export default function PosPage({ clients, user }) {
  const [rows, setRows] = useState([]);
  const [limits, setLimits] = useState(null);
  const [services, setServices] = useState(FALLBACK_SERVICES);
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(() => practiceApi("/pos/consultancies").then(setRows).catch(err => setError(err.message)), []);
  useEffect(() => {
    load();
    practiceApi("/pos/thresholds").then(setLimits).catch(() => {});
    practiceApi("/pos/catalog").then(data => setServices(data.service_types || FALLBACK_SERVICES)).catch(() => {});
  }, [load]);

  const { sort, onSort } = useSortPreference("conventions", DEFAULT_SORT);
  const visible = useMemo(() => sortRows(rows.filter(r => filter === "all" || r.status === filter), sort?.key ? sort : DEFAULT_SORT), [rows, filter, sort]);
  const available = useMemo(() => clients.filter(c => !rows.some(r => r.client_id === c.id)), [clients, rows]);

  async function create(event) {
    event.preventDefault();
    setBusy(true); setError("");
    try {
      const created = await practiceApi("/pos/consultancies", { method: "POST", body: JSON.stringify({ data: draft }) });
      setDraft(null); await load(); setOpenId(created.id);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }
  async function saveLimits(changes) {
    try { setLimits(await practiceApi("/pos/thresholds", { method: "PUT", body: JSON.stringify({ data: changes }) })); await load(); }
    catch (err) { setError(err.message); }
  }
  async function remove(row) {
    if (!window.confirm(`Rimuovere ${row.client_name} dal workspace POS e PiMUS? Verranno eliminati anche i documenti collegati.`)) return;
    try { await practiceApi(`/pos/consultancies/${row.id}`, { method: "DELETE" }); setOpenId(null); await load(); }
    catch (err) { setError(err.message); }
  }

  if (openId) return <ConsultancyDetail id={openId} user={user} services={services} onBack={() => { setOpenId(null); load(); }} onDeleted={() => { setOpenId(null); load(); }} />;

  return <section className="pos-page" data-testid="pos-page">
    <div className="practice-action-bar">
      <span className="muted" data-testid="pos-count">{visible.length} aziende in convenzione</span>
      <button className="primary" data-testid="pos-create-button" onClick={() => setDraft({ client_id: "", referent: "", referent_phone: "", referent_email: "", service_type: services[0], service_detail: "", billing_cycle: "Annuale", fee_amount: "", start_date: "", last_renewal_date: "", notes: "" })}>
        <Plus size={15} />Nuova convenzione
      </button>
    </div>
    {error && <div className="error" role="alert" data-testid="pos-error">{error}</div>}

    <div className="pos-filters" data-testid="pos-filters">
      {FILTERS.map(([key, label]) => <button key={key} className={filter === key ? "active" : ""} data-testid={`pos-filter-${key.toLowerCase().replace(" ", "-")}`} onClick={() => setFilter(key)}>{label}</button>)}
      {limits && user.role === "admin" && <div className="pos-thresholds" data-testid="pos-thresholds">
        <label>Soglia in scadenza (gg)<input type="number" min="1" max="365" data-testid="pos-threshold-warning" defaultValue={limits.warning_days}
          onBlur={e => Number(e.target.value) !== limits.warning_days && saveLimits({ ...limits, warning_days: Number(e.target.value) })} /></label>
        <label>Soglia urgente (gg)<input type="number" min="1" max="365" data-testid="pos-threshold-urgent" defaultValue={limits.urgent_days}
          onBlur={e => Number(e.target.value) !== limits.urgent_days && saveLimits({ ...limits, urgent_days: Number(e.target.value) })} /></label>
      </div>}
    </div>

    <div className="pos-scroll">
      <table className="pos-table" data-testid="pos-table">
        <thead><tr>
          {COLUMNS.map(([key, label, kind, extra]) => <SortableTh key={key} label={label} sortKey={key} kind={kind}
            sort={sort} onSort={onSort} className={extra || ""} testId={`pos-sort-${key}`} />)}
          <th />
        </tr></thead>
        <tbody>
          {visible.map(row => <tr key={row.id} data-testid={`pos-row-${row.id}`}>
            <th><button className="link" data-testid={`pos-open-${row.id}`} onClick={() => setOpenId(row.id)}>{row.client_name}<ArrowUpRight size={13} /></button></th>
            <td>{row.referent || "—"}</td>
            <td>{formatDate(row.start_date)}</td>
            <td>{formatDate(row.last_renewal_date)}</td>
            <td data-testid={`pos-due-${row.id}`}>{formatDate(row.next_due_date)}</td>
            <td className="numeric" data-testid={`pos-days-${row.id}`}>{row.days_remaining ?? "—"}</td>
            <td>{row.service_type}{row.service_detail ? <small className="pos-detail-note">{row.service_detail}</small> : null}</td>
            <td><StatusBadge status={row.status} testId={`pos-status-${row.id}`} /></td>
            <td data-testid={`pos-docs-${row.id}`}>{row.documents_count ? `${row.documents_count} documenti` : "—"}</td>
            <td>{(user.role === "admin" || row.assigned_to === user.id) && <button className="icon-btn" title="Rimuovi consulenza" data-testid={`pos-delete-${row.id}`} onClick={() => remove(row)}><Trash2 size={14} /></button>}</td>
          </tr>)}
        </tbody>
      </table>
      {!visible.length && <div className="practice-empty" data-testid="pos-empty"><HardHat size={28} /><strong>{rows.length ? "Nessuna convenzione con questo filtro" : "Nessuna convenzione attiva"}</strong><span>Richiama un'azienda dall'anagrafica clienti e scegli il tipo di convenzione.</span></div>}
    </div>

    {draft && <div className="modal-backdrop"><form className="create-modal" onSubmit={create} data-testid="pos-create-modal">
      <button type="button" className="modal-close" data-testid="pos-create-close" onClick={() => setDraft(null)}><X /></button>
      <p className="eyebrow">CONVENZIONI</p>
      <h2>Nuova convenzione</h2>
      <label>Azienda (dall'anagrafica clienti)
        <select required data-testid="pos-draft-client" value={draft.client_id} onChange={e => setDraft({ ...draft, client_id: e.target.value })}>
          <option value="">Seleziona l'azienda</option>
          {[...available].sort((a, b) => (a.name || "").localeCompare(b.name || "", "it")).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </label>
      <label>Referente (cognome e nome)<input data-testid="pos-draft-referent" value={draft.referent} onChange={e => setDraft({ ...draft, referent: e.target.value })} /></label>
      <label>Telefono referente<input data-testid="pos-draft-phone" value={draft.referent_phone} onChange={e => setDraft({ ...draft, referent_phone: e.target.value })} /></label>
      <label>Email referente<input data-testid="pos-draft-email" value={draft.referent_email} onChange={e => setDraft({ ...draft, referent_email: e.target.value })} /></label>
      <label>Tipo di convenzione
        <select data-testid="pos-draft-service" value={draft.service_type} onChange={e => setDraft({ ...draft, service_type: e.target.value })}>
          {services.map(service => <option key={service} value={service}>{service}</option>)}
        </select>
      </label>
      <label>Descrizione della convenzione{draft.service_type === "Altro" ? " (obbligatoria)" : " (facoltativa)"}
        <textarea rows={2} required={draft.service_type === "Altro"} data-testid="pos-draft-service-detail" value={draft.service_detail}
          onChange={e => setDraft({ ...draft, service_detail: e.target.value })} placeholder="Es. redazione POS e PiMUS con aggiornamento annuale" />
      </label>
      <label>Cadenza di pagamento
        <select data-testid="pos-draft-cycle" value={draft.billing_cycle} onChange={e => setDraft({ ...draft, billing_cycle: e.target.value })}>
          {CYCLES.map(cycle => <option key={cycle} value={cycle}>{cycle}</option>)}
        </select>
      </label>
      <label>Compenso concordato (€)<input type="number" min="0" step="0.01" data-testid="pos-draft-fee" value={draft.fee_amount} onChange={e => setDraft({ ...draft, fee_amount: e.target.value })} /></label>
      <label>Data inizio convenzione<input type="date" data-testid="pos-draft-start" value={draft.start_date} onChange={e => setDraft({ ...draft, start_date: e.target.value })} /></label>
      <label>Data ultimo rinnovo<input type="date" data-testid="pos-draft-renewal" value={draft.last_renewal_date} onChange={e => setDraft({ ...draft, last_renewal_date: e.target.value })} /></label>
      <label>Note<textarea rows={2} data-testid="pos-draft-notes" value={draft.notes} onChange={e => setDraft({ ...draft, notes: e.target.value })} /></label>
      <button className="primary wide" type="submit" disabled={busy} data-testid="pos-draft-save">{busy ? "Salvataggio…" : "Crea convenzione"}</button>
    </form></div>}
  </section>;
}

const ConsultancyDetail = ({ id, user, services, onBack, onDeleted }) => {
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(null);
  const [newDoc, setNewDoc] = useState(null);
  const [openDoc, setOpenDoc] = useState(null);
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const data = await practiceApi(`/pos/consultancies/${id}`);
      setDetail(data); setForm(data.consultancy);
    } catch (err) { setError(err.message); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  async function saveConsultancy() {
    setBusy("save"); setError(""); setNotice("");
    try {
      const keys = ["referent", "referent_phone", "referent_email", "service_type", "service_detail", "billing_cycle", "fee_amount", "start_date", "last_renewal_date", "next_due_date", "notes"];
      await practiceApi(`/pos/consultancies/${id}`, { method: "PUT", body: JSON.stringify({ data: Object.fromEntries(keys.map(k => [k, form[k] ?? ""])) }) });
      setNotice("Consulenza aggiornata"); await load();
    } catch (err) { setError(err.message); } finally { setBusy(""); }
  }
  async function createDocument(event) {
    event.preventDefault();
    setBusy("doc"); setError("");
    try {
      const created = await practiceApi(`/pos/consultancies/${id}/documents`, { method: "POST", body: JSON.stringify({ data: newDoc }) });
      setNewDoc(null); await load(); setOpenDoc(created);
      setNotice(`${created.kind} ${created.year} rev. ${created.revision} creato · ${created.origin}`);
    } catch (err) { setError(err.message); } finally { setBusy(""); }
  }
  async function saveDocument() {
    setBusy("docsave"); setError("");
    try {
      const saved = await practiceApi(`/pos/documents/${openDoc.id}`, { method: "PUT", body: JSON.stringify({ data: {
        status: openDoc.status, site: openDoc.site, principal: openDoc.principal, notes: openDoc.notes, date: openDoc.date, content: openDoc.content } }) });
      setOpenDoc(saved); await load(); setNotice("Revisione salvata");
    } catch (err) { setError(err.message); } finally { setBusy(""); }
  }
  async function removeDocument(document) {
    if (!window.confirm(`Eliminare ${document.kind} ${document.year} rev. ${document.revision}?`)) return;
    try { await practiceApi(`/pos/documents/${document.id}`, { method: "DELETE" }); setOpenDoc(null); await load(); }
    catch (err) { setError(err.message); }
  }
  async function removeConsultancy() {
    if (!window.confirm("Rimuovere la convenzione e tutti i documenti collegati?")) return;
    try { await practiceApi(`/pos/consultancies/${id}`, { method: "DELETE" }); onDeleted(); }
    catch (err) { setError(err.message); }
  }

  if (!detail || !form) return null;

  return <section className="pos-page practice-workspace" data-testid="pos-detail">
    <div className="practice-action-bar">
      <button className="ghost" data-testid="pos-back" onClick={onBack}>← Tutte le convenzioni</button>
      <div className="practice-inline-actions">
        {user.role === "admin" && <button className="ghost" data-testid="pos-detail-delete" onClick={removeConsultancy}><Trash2 size={15} />Rimuovi convenzione</button>}
        <button className="ghost" data-testid="pos-new-pos" onClick={() => setNewDoc({ kind: "POS", mode: "blank", year: new Date().getFullYear(), site: "", principal: "", notes: "" })}><Plus size={15} />Nuovo POS</button>
        <button className="ghost" data-testid="pos-new-pimus" onClick={() => setNewDoc({ kind: "PiMUS", mode: "blank", year: new Date().getFullYear(), site: "", principal: "", notes: "" })}><Plus size={15} />Nuovo PiMUS</button>
        <button className="primary" data-testid="pos-save" disabled={!!busy} onClick={saveConsultancy}><Save size={15} />{busy === "save" ? "Salvataggio…" : "Salva dati"}</button>
      </div>
    </div>
    {error && <div className="error" role="alert" data-testid="pos-detail-error">{error}</div>}
    {notice && <div className="flash" data-testid="pos-detail-notice">{notice}</div>}

    <header className="practice-section-heading">
      <h2><HardHat size={17} />{form.client_name}</h2>
      <span>{form.service_type}{form.billing_cycle ? ` · pagamento ${form.billing_cycle.toLowerCase()}` : ""} · prossima scadenza {formatDate(form.next_due_date)} · {form.days_remaining ?? "—"} giorni · <StatusBadge status={form.status} testId="pos-detail-status" /></span>
    </header>

    <fieldset className="practice-fieldset"><legend>Dati della convenzione</legend><div className="practice-form-grid">
      <label>Referente (cognome e nome)<input data-testid="pos-field-referent" value={form.referent || ""} onChange={e => setForm({ ...form, referent: e.target.value })} /></label>
      <label>Telefono<input data-testid="pos-field-phone" value={form.referent_phone || ""} onChange={e => setForm({ ...form, referent_phone: e.target.value })} /></label>
      <label>Email<input data-testid="pos-field-email" value={form.referent_email || ""} onChange={e => setForm({ ...form, referent_email: e.target.value })} /></label>
      <label>Tipo di convenzione<select data-testid="pos-field-service" value={form.service_type} onChange={e => setForm({ ...form, service_type: e.target.value })}>
        {[...new Set([...(services || []), form.service_type].filter(Boolean))].map(service => <option key={service} value={service}>{service}</option>)}
      </select></label>
      <label>Cadenza di pagamento<select data-testid="pos-field-cycle" value={form.billing_cycle || "Annuale"} onChange={e => setForm({ ...form, billing_cycle: e.target.value })}>
        {CYCLES.map(cycle => <option key={cycle} value={cycle}>{cycle}</option>)}
      </select></label>
      <label>Compenso concordato (€)<input type="number" min="0" step="0.01" data-testid="pos-field-fee" value={form.fee_amount ?? ""} onChange={e => setForm({ ...form, fee_amount: e.target.value })} /></label>
      <label className="field-wide">Descrizione della convenzione<textarea rows={2} data-testid="pos-field-service-detail" value={form.service_detail || ""} onChange={e => setForm({ ...form, service_detail: e.target.value })} /></label>
      <label>Data inizio convenzione<input type="date" data-testid="pos-field-start" value={form.start_date || ""} onChange={e => setForm({ ...form, start_date: e.target.value })} /></label>
      <label>Data ultimo rinnovo<input type="date" data-testid="pos-field-renewal" value={form.last_renewal_date || ""} onChange={e => setForm({ ...form, last_renewal_date: e.target.value })} /></label>
      <label>Scadenza manuale (facoltativa)<input type="date" data-testid="pos-field-due" value={form.next_due_date || ""} onChange={e => setForm({ ...form, next_due_date: e.target.value })} /></label>
      <label className="field-wide">Note<textarea rows={2} data-testid="pos-field-notes" value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} /></label>
    </div></fieldset>

    <div className="pos-dossier" data-testid="pos-dossier">
      {detail.years.map(group => <div className="pos-year" key={group.year} data-testid={`pos-year-${group.year}`}>
        <strong><FolderOpen size={14} />{group.year}</strong>
        {group.documents.map(document => <button key={document.id} className="pos-doc" data-testid={`pos-doc-${document.id}`} onClick={() => setOpenDoc(document)}>
          <span>{document.kind} · rev. {document.revision}</span>
          <StatusBadge status={document.status} testId={`pos-doc-status-${document.id}`} />
          <small>{formatDate(document.date)} · {document.origin}</small>
        </button>)}
      </div>)}
      {!detail.years.length && <p className="practice-hint" data-testid="pos-dossier-empty">Nessun documento: crea il primo POS o PiMUS con i pulsanti in alto.</p>}
    </div>

    {newDoc && <div className="modal-backdrop"><form className="create-modal" onSubmit={createDocument} data-testid="pos-doc-modal">
      <button type="button" className="modal-close" data-testid="pos-doc-close" onClick={() => setNewDoc(null)}><X /></button>
      <p className="eyebrow">NUOVO DOCUMENTO</p>
      <h2>Nuovo {newDoc.kind}</h2>
      <label>Come procedere
        <select data-testid="pos-doc-mode" value={newDoc.mode} onChange={e => setNewDoc({ ...newDoc, mode: e.target.value })}>
          {MODES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </label>
      <label>Anno<input type="number" min="2020" max="2100" data-testid="pos-doc-year" value={newDoc.year} onChange={e => setNewDoc({ ...newDoc, year: e.target.value })} /></label>
      <label>Cantiere<input data-testid="pos-doc-site" value={newDoc.site} onChange={e => setNewDoc({ ...newDoc, site: e.target.value })} /></label>
      <label>Committente<input data-testid="pos-doc-principal" value={newDoc.principal} onChange={e => setNewDoc({ ...newDoc, principal: e.target.value })} /></label>
      <label>Note<input data-testid="pos-doc-notes" value={newDoc.notes} onChange={e => setNewDoc({ ...newDoc, notes: e.target.value })} /></label>
      <button className="primary wide" type="submit" disabled={!!busy} data-testid="pos-doc-save">
        {newDoc.mode === "ai" ? <Sparkles size={15} /> : <Plus size={15} />}{busy === "doc" ? "Creazione in corso…" : `Crea ${newDoc.kind}`}
      </button>
    </form></div>}

    {openDoc && <div className="modal-backdrop"><div className="create-modal pos-doc-modal" data-testid="pos-doc-editor">
      <button type="button" className="modal-close" data-testid="pos-doc-editor-close" onClick={() => setOpenDoc(null)}><X /></button>
      <p className="eyebrow">{openDoc.kind} {openDoc.year} · REV. {openDoc.revision}</p>
      <h2>{form.client_name}</h2>
      <small className="pos-doc-origin">{openDoc.origin}</small>
      <div className="practice-form-grid">
        <label>Stato<select data-testid="pos-doc-editor-status" value={openDoc.status} onChange={e => setOpenDoc({ ...openDoc, status: e.target.value })}>
          {detail.statuses.map(status => <option key={status} value={status}>{status}</option>)}
        </select></label>
        <label>Data<input type="date" data-testid="pos-doc-editor-date" value={openDoc.date || ""} onChange={e => setOpenDoc({ ...openDoc, date: e.target.value })} /></label>
        <label>Cantiere<input data-testid="pos-doc-editor-site" value={openDoc.site || ""} onChange={e => setOpenDoc({ ...openDoc, site: e.target.value })} /></label>
        <label>Committente<input data-testid="pos-doc-editor-principal" value={openDoc.principal || ""} onChange={e => setOpenDoc({ ...openDoc, principal: e.target.value })} /></label>
        <label className="field-wide">Note<textarea rows={2} data-testid="pos-doc-editor-notes" value={openDoc.notes || ""} onChange={e => setOpenDoc({ ...openDoc, notes: e.target.value })} /></label>
        {SECTION_LABELS.map(([key, label]) => <label className="field-wide" key={key}>{label}
          <textarea rows={3} data-testid={`pos-doc-section-${key}`} value={(openDoc.content || {})[key] || ""}
            onChange={e => setOpenDoc({ ...openDoc, content: { ...openDoc.content, [key]: e.target.value } })} />
        </label>)}
      </div>
      <div className="practice-inline-actions">
        <button className="ghost" data-testid="pos-doc-editor-delete" onClick={() => removeDocument(openDoc)}><Trash2 size={15} />Elimina revisione</button>
        <button className="primary" data-testid="pos-doc-editor-save" disabled={!!busy} onClick={saveDocument}><Save size={15} />{busy === "docsave" ? "Salvataggio…" : "Salva revisione"}</button>
      </div>
    </div></div>}
  </section>;
};
